'use client';

import { MarkdownPreview, Text } from '@terra/ui-design-system';
import { Box, VStack } from '@terra/ui-styled-system';
import type { CoPilotAgentMessageDebugEntry } from '@terra/shared-dto-chat-types';
import {
  extractReadableText,
  isLikelyMarkdown,
  normalizePayloadText,
} from './agentMessageViewUtils';

type AgentMessageReadableContentProps = {
  entry?: CoPilotAgentMessageDebugEntry | null;
  text?: string;
  compact?: boolean;
  maxHeight?: string;
};

export function AgentMessageReadableContent({
  entry,
  text,
  compact = false,
  maxHeight = '420px',
}: AgentMessageReadableContentProps) {
  const resolvedText =
    text ?? (entry ? extractReadableText(entry.content) : '') ?? '';

  const displayText = normalizePayloadText(resolvedText);

  if (!displayText.trim()) {
    return (
      <Text fontSize="sm" color="fg.muted" fontStyle="italic">
        Empty content
      </Text>
    );
  }

  if (isLikelyMarkdown(displayText)) {
    return (
      <Box
        maxH={maxHeight}
        overflow="auto"
        px={compact ? 2 : 3}
        py={compact ? 2 : 3}
        bg="bg.subtle"
        borderRadius="md"
        borderWidth="1px"
        borderColor="border.subtle"
      >
        <MarkdownPreview
          source={displayText}
          fontSize={compact ? 12 : 13}
          compact
        />
      </Box>
    );
  }

  return (
    <Box
      maxH={maxHeight}
      overflow="auto"
      px={compact ? 2 : 3}
      py={compact ? 2 : 3}
      bg="bg.subtle"
      borderRadius="md"
      borderWidth="1px"
      borderColor="border.subtle"
    >
      <Text
        as="pre"
        fontSize={compact ? 'xs' : 'sm'}
        fontFamily="mono"
        whiteSpace="pre-wrap"
        wordBreak="break-word"
        m={0}
        color="fg.default"
        lineHeight="1.6"
      >
        {displayText}
      </Text>
    </Box>
  );
}

type ContentBlockListProps = {
  entry: CoPilotAgentMessageDebugEntry;
};

export function AgentMessageContentBlocks({ entry }: ContentBlockListProps) {
  if (!Array.isArray(entry.content) || entry.content.length === 0) {
    return <AgentMessageReadableContent entry={entry} />;
  }

  return (
    <VStack alignItems="stretch" gap={2}>
      {entry.content.map((block, blockIndex) => {
        const blockType =
          typeof block.type === 'string' ? block.type : 'unknown';

        if (blockType === 'image_url') {
          return (
            <Box
              key={`${blockType}-${blockIndex}`}
              px={3}
              py={2}
              borderRadius="md"
              borderWidth="1px"
              borderColor="border.subtle"
              bg="bg.subtle"
            >
              <Text fontSize="xs" color="fg.muted" fontFamily="mono">
                image block
              </Text>
            </Box>
          );
        }

        if (
          block.cachePoint !== undefined ||
          block.cache_control !== undefined
        ) {
          return (
            <Box
              key={`cache-${blockIndex}`}
              px={3}
              py={2}
              borderRadius="md"
              borderWidth="1px"
              borderColor="border.subtle"
              bg="bg.muted"
            >
              <Text fontSize="xs" color="fg.muted" fontFamily="mono">
                cache point
              </Text>
            </Box>
          );
        }

        const blockText =
          typeof block.text === 'string'
            ? normalizePayloadText(block.text)
            : '';

        return (
          <Box key={`${blockType}-${blockIndex}`}>
            {entry.content.length > 1 && (
              <Text
                fontSize="2xs"
                color="fg.muted"
                fontFamily="mono"
                mb={1}
                textTransform="uppercase"
              >
                {blockType}
              </Text>
            )}
            <AgentMessageReadableContent text={blockText} compact />
          </Box>
        );
      })}
    </VStack>
  );
}
